import { toDateOnlyString } from "../../../components/common/employeeUtils";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isUuid(value) {
  return UUID_RE.test(String(value || "").trim());
}

export function employeeDisplayName(emp) {
  if (!emp) return "";
  const explicit = String(emp.display_name || "").trim();
  if (explicit) return explicit;
  return [emp.first_name, emp.middle_name, emp.last_name].filter(Boolean).join(" ").trim();
}

/** Title-case display for profile hero (keeps acronyms reasonable). */
export function employeeDisplayNameFormatted(emp) {
  const raw = employeeDisplayName(emp);
  if (!raw) return "";
  return raw
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

export function formatProfileDate(value) {
  const iso = toDateOnlyString(value);
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export function formatStatusLabel(status) {
  if (!status) return "Unknown";
  return String(status)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatCurrencyInr(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function toDays(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Leave balance rows from /leave/balances use consumed / available (Decimal strings). */
export function leaveBalanceUsed(b) {
  return toDays(b?.used ?? b?.consumed);
}

export function leaveBalanceRemaining(b) {
  return toDays(b?.remaining ?? b?.balance ?? b?.available);
}

export function leaveBalanceEntitled(b) {
  const explicit = toDays(b?.entitlement ?? b?.allocated);
  if (explicit != null) return explicit;
  const parts = [b?.opening_balance, b?.accrued, b?.carried_forward].map(toDays);
  if (parts.every((p) => p == null)) return null;
  return parts.reduce((s, p) => s + (p || 0), 0);
}

export function formatDays(value) {
  const n = toDays(value);
  if (n == null) return "—";
  return String(Math.round(n * 100) / 100);
}

export function computeTenure(dateOfJoining) {
  const iso = toDateOnlyString(dateOfJoining);
  if (!iso) return null;
  const start = new Date(`${iso}T00:00:00`);
  const now = new Date();
  if (Number.isNaN(start.getTime()) || start > now) return null;

  let months =
    (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months -= 1;
  if (months < 0) months = 0;

  const years = Math.floor(months / 12);
  const remMonths = months % 12;
  const parts = [];
  if (years) parts.push(`${years} year${years === 1 ? "" : "s"}`);
  if (remMonths) parts.push(`${remMonths} month${remMonths === 1 ? "" : "s"}`);
  if (!parts.length) parts.push("Less than 1 month");
  return { label: parts.join(" "), months, years };
}

export function managerLabel(emp) {
  const rm = emp?.reporting_manager_employee;
  if (rm?.employee_name) return rm.employee_name;
  if (emp?.reporting_manager?.full_name) return emp.reporting_manager.full_name;
  if (emp?.manager?.first_name) {
    return [emp.manager.first_name, emp.manager.last_name].filter(Boolean).join(" ");
  }
  return "—";
}

export function designationLabel(emp) {
  return emp?.designation?.title || emp?.designation?.name || "—";
}

export function departmentLabel(emp) {
  return emp?.department?.name || "—";
}

export function locationLabel(emp) {
  return emp?.location?.name || "—";
}

export function getInitials(emp) {
  const name = employeeDisplayName(emp);
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function avatarHue(name) {
  let hash = 0;
  const s = String(name || "");
  for (let i = 0; i < s.length; i += 1) hash = s.charCodeAt(i) + ((hash << 5) - hash);
  const hues = ["#4f46e5", "#0f766e", "#0369a1", "#7c3aed", "#c2410c", "#15803d"];
  return hues[Math.abs(hash) % hues.length];
}

export function monthRangeISO(year, monthIndex) {
  const from = new Date(year, monthIndex, 1);
  const to = new Date(year, monthIndex + 1, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return {
    from_date: `${from.getFullYear()}-${pad(from.getMonth() + 1)}-${pad(from.getDate())}`,
    to_date: `${to.getFullYear()}-${pad(to.getMonth() + 1)}-${pad(to.getDate())}`,
  };
}

export function summarizeAttendanceRows(rows) {
  const summary = {
    workingDays: 0,
    present: 0,
    absent: 0,
    late: 0,
    halfDay: 0,
    leave: 0,
    holiday: 0,
    weekOff: 0,
    overtime: 0,
  };

  for (const row of rows || []) {
    summary.workingDays += 1;
    const status = String(row.status || "").toLowerCase();
    if (status === "present") summary.present += 1;
    else if (status === "absent") summary.absent += 1;
    else if (status === "half-day" || status === "half_day") summary.halfDay += 1;
    else if (status === "leave") summary.leave += 1;
    else if (status === "holiday") summary.holiday += 1;
    else if (status === "week-off" || status === "week_off") summary.weekOff += 1;
    if (row.late || row.isLate) summary.late += 1;
    if (row.overtime || row.overtimeHours > 0) summary.overtime += 1;
  }

  const countable = summary.present + summary.halfDay * 0.5;
  const denom = Math.max(
    1,
    summary.workingDays - summary.holiday - summary.weekOff
  );
  summary.attendancePct = Math.round((countable / denom) * 1000) / 10;
  return summary;
}

export function maskSensitive(value, { visible = false, showLast = 4 } = {}) {
  const s = String(value || "").trim();
  if (!s) return "—";
  if (visible) return s;
  if (s.length <= showLast + 2) return "••••••";
  return `${s.slice(0, Math.min(5, s.length - showLast))}${"•".repeat(3)}${s.slice(-showLast)}`;
}

export function buildLifecycleEvents(employee) {
  if (!employee) return [];
  const events = [];
  if (employee.created_at) {
    events.push({
      id: "created",
      date: employee.created_at,
      title: "Employee Created",
      description: "Employee record created in People Hub",
    });
  }
  if (employee.date_of_joining) {
    events.push({
      id: "joined",
      date: employee.date_of_joining,
      title: "Joined Organization",
      description: `Joined as ${designationLabel(employee)}`,
    });
  }
  if (employee.date_of_leaving) {
    events.push({
      id: "exit",
      date: employee.date_of_leaving,
      title: "Exit / Last Working Day",
      description: employee.status === "terminated" ? "Employment ended" : "Recorded leaving date",
    });
  }
  return events.sort((a, b) => String(a.date).localeCompare(String(b.date)));
}
